import { useCallback, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
    showLoadingNotification,
    updateSuccessNotification,
    updateFailedNotification,
} from '../notifications';
import { downloadBlob } from '../utility/downloadBlob';
import {
    excelExportHttpClient,
    getExportErrorMessage,
} from '../services/excelExportHttpClient';

export type ExcelExportNotificationId<TArgs> =
    | string
    | ((args: TArgs) => string);

export interface UseExcelExportConfig<TArgs> {
    notificationId: ExcelExportNotificationId<TArgs>;
    loadingTitle: string;
    loadingMessage: string;
    successTitle: string;
    successMessage: string;
    failedTitle: string;
    filenamePrefix: string;
    exportPath: string;
    buildBody: (args: TArgs) => unknown;
    validate?: (args: TArgs) => string | null;
    throwOnError?: boolean;
}

export interface UseExcelExportResult<TArgs> {
    exporting: boolean;
    exportError: Error | undefined;
    runExport: (args: TArgs) => Promise<void>;
}

function resolveNotificationId<TArgs>(
    notificationId: ExcelExportNotificationId<TArgs>,
    args: TArgs,
): string {
    return typeof notificationId === 'function'
        ? notificationId(args)
        : notificationId;
}

export function useExcelExport<TArgs>(
    config: UseExcelExportConfig<TArgs>,
): UseExcelExportResult<TArgs> {
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState<Error | undefined>(
        undefined,
    );
    const configRef = useRef(config);
    configRef.current = config;

    const runExport = useCallback(async (args: TArgs) => {
        const exportConfig = configRef.current;
        const notificationId = resolveNotificationId(
            exportConfig.notificationId,
            args,
        );

        showLoadingNotification(
            notificationId,
            exportConfig.loadingTitle,
            exportConfig.loadingMessage,
        );

        const invalidReason = exportConfig.validate?.(args);
        if (invalidReason) {
            updateFailedNotification(
                notificationId,
                exportConfig.failedTitle,
                invalidReason,
            );
            const validationError = new Error(invalidReason);
            setExportError(validationError);
            if (exportConfig.throwOnError) {
                throw validationError;
            }
            return;
        }

        setExportError(undefined);
        setExporting(true);

        try {
            const blob = await excelExportHttpClient.postBlob(
                exportConfig.exportPath,
                exportConfig.buildBody(args),
            );
            const filename = `${exportConfig.filenamePrefix}_${dayjs().format(
                'YYYY-MM-DD_HH-mm-ss',
            )}.xlsx`;
            downloadBlob(blob, filename);
            updateSuccessNotification(
                notificationId,
                exportConfig.successTitle,
                exportConfig.successMessage,
            );
        } catch (error: unknown) {
            const message = await getExportErrorMessage(error);
            console.error('Error during Excel export:', message);
            const caughtError = new Error(message);
            setExportError(caughtError);
            updateFailedNotification(
                notificationId,
                exportConfig.failedTitle,
                message,
            );
            if (exportConfig.throwOnError) {
                throw caughtError;
            }
        } finally {
            setExporting(false);
        }
    }, []);

    return { exporting, exportError, runExport };
}
